const ZONE_TO_KEY = {
  dimensions: 'dimensions',
  metrics: 'metrics',
  filters: 'filters',
  sort: 'sort',
};

const createUiId = (prefix) =>
  `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const normalizeStringArray = (value) =>
  Array.isArray(value) ? value.map((item) => String(item).toLowerCase()) : [];

const toRuleBase = (field) => ({
  uiId: createUiId('rule'),
  fieldKey: field.fieldKey,
  label: field.label,
  entity: field.entity,
  entityKey: field.entityKey,
  dataType: field.dataType,
  selectable: Boolean(field.selectable),
  filterable: Boolean(field.filterable),
  groupable: Boolean(field.groupable),
  sortable: Boolean(field.sortable),
  supportsValueLookup: Boolean(field.supportsValueLookup),
  supportedOperators: normalizeStringArray(field.supportedOperators),
  supportedAggregates: normalizeStringArray(field.supportedAggregates),
  alias: '',
});

const createZoneRule = (field, zone) => {
  const base = toRuleBase(field);

  if (zone === 'metrics') {
    const defaultAggregation =
      base.supportedAggregates[0] || (base.dataType === 'number' ? 'sum' : 'count');
    return {
      ...base,
      aggregation: defaultAggregation,
      distinct: false,
    };
  }

  if (zone === 'filters') {
    const defaultOperator = base.supportedOperators[0] || 'eq';
    return {
      ...base,
      operator: defaultOperator,
      values: [],
    };
  }

  if (zone === 'sort') {
    return {
      ...base,
      direction: 'asc',
    };
  }

  return base;
};

const moveItem = (list, sourceIndex, targetIndex) => {
  if (
    sourceIndex < 0 ||
    targetIndex < 0 ||
    sourceIndex >= list.length ||
    targetIndex >= list.length
  ) {
    return list;
  }
  const next = [...list];
  const [item] = next.splice(sourceIndex, 1);
  next.splice(targetIndex, 0, item);
  return next;
};

const createPreviewState = () => ({
  status: 'idle',
  columns: [],
  rows: [],
  errorMessage: '',
  recordsFound: 0,
  rowCount: 0,
  executionMs: 0,
});

const createDefinition = () => ({
  name: '',
  description: '',
  dimensions: [],
  metrics: [],
  filters: [],
  sort: [],
  page: 1,
  pageSize: 100,
});

export const initialBuilderState = {
  definition: createDefinition(),
  selectedChip: null,
  zoneErrors: {
    dimensions: '',
    metrics: '',
    filters: '',
    sort: '',
  },
  summaryErrors: [],
  warnings: [],
  operatorCatalog: [],
  preview: createPreviewState(),
};

export const builderReducer = (state, action) => {
  switch (action.type) {
    case 'SET_DEFINITION_KEY': {
      const { key, value } = action.payload;
      return {
        ...state,
        definition: {
          ...state.definition,
          [key]: value,
        },
      };
    }

    case 'DROP_NEW_FIELD': {
      const { field, zone } = action.payload;
      const key = ZONE_TO_KEY[zone];
      if (!key) return state;
      const nextItem = createZoneRule(field, key);
      return {
        ...state,
        definition: {
          ...state.definition,
          [key]: [...state.definition[key], nextItem],
        },
        zoneErrors: {
          ...state.zoneErrors,
          [key]: '',
        },
      };
    }

    case 'SET_ZONE_ERROR': {
      const { zone, message } = action.payload;
      return {
        ...state,
        zoneErrors: {
          ...state.zoneErrors,
          [zone]: message,
        },
      };
    }

    case 'SELECT_CHIP': {
      return {
        ...state,
        selectedChip: action.payload,
      };
    }

    case 'UPDATE_CHIP': {
      const { zone, uiId, updates } = action.payload;
      const key = ZONE_TO_KEY[zone];
      if (!key) return state;
      return {
        ...state,
        definition: {
          ...state.definition,
          [key]: state.definition[key].map((item) =>
            item.uiId === uiId ? { ...item, ...updates } : item
          ),
        },
      };
    }

    case 'REMOVE_CHIP': {
      const { zone, uiId } = action.payload;
      const key = ZONE_TO_KEY[zone];
      if (!key) return state;
      const selectedMatches =
        state.selectedChip?.zone === zone && state.selectedChip?.uiId === uiId;
      return {
        ...state,
        definition: {
          ...state.definition,
          [key]: state.definition[key].filter((item) => item.uiId !== uiId),
        },
        selectedChip: selectedMatches ? null : state.selectedChip,
      };
    }

    case 'REORDER_ZONE': {
      const { zone, sourceIndex, targetIndex } = action.payload;
      const key = ZONE_TO_KEY[zone];
      if (!key) return state;
      return {
        ...state,
        definition: {
          ...state.definition,
          [key]: moveItem(state.definition[key], sourceIndex, targetIndex),
        },
      };
    }

    case 'SET_VALIDATION': {
      const { summaryErrors, zoneErrors, warnings } = action.payload;
      return {
        ...state,
        summaryErrors: summaryErrors || [],
        warnings: warnings || [],
        zoneErrors: {
          ...state.zoneErrors,
          ...(zoneErrors || {}),
        },
      };
    }

    case 'SET_OPERATOR_CATALOG': {
      return {
        ...state,
        operatorCatalog: Array.isArray(action.payload) ? action.payload : [],
      };
    }

    case 'SET_PREVIEW': {
      return {
        ...state,
        preview: {
          ...state.preview,
          ...action.payload,
        },
      };
    }

    case 'RESET_PREVIEW': {
      return {
        ...state,
        preview: createPreviewState(),
      };
    }

    case 'LOAD_REPORT': {
      const report = action.payload;
      return {
        ...state,
        definition: {
          ...createDefinition(),
          ...report.definition,
        },
        selectedChip: null,
        zoneErrors: {
          dimensions: '',
          metrics: '',
          filters: '',
          sort: '',
        },
        summaryErrors: [],
        warnings: [],
        preview: createPreviewState(),
      };
    }

    default:
      return state;
  }
};
